import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, doc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';

const SupportTickets = () => {
  const { currentUser } = useAuth();
  const { createNotification, getAdminUserIds } = useNotifications();
  const [tickets, setTickets] = useState([]);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [notification, setNotification] = useState(null);
  
  // Chat states
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagesListener, setMessagesListener] = useState(null);
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadTickets();
    }
  }, [currentUser]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadTickets = async () => {
    if (!currentUser) return;

    setLoadingTickets(true);
    try {
      const ticketsRef = collection(db, 'tickets');
      let querySnapshot;
      
      // Try with orderBy first, fallback to simple query if it fails
      try {
        const q = query(
          ticketsRef,
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        console.warn('OrderBy failed, trying simple query:', orderError);
        // Fallback to simple query without orderBy
        const q = query(
          ticketsRef,
          where('userId', '==', currentUser.uid)
        );
        querySnapshot = await getDocs(q);
      }
      
      const ticketsList = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        ticketsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          createdDate: data.createdAt?.toDate ? 
            data.createdAt.toDate().toISOString().split('T')[0] : 
            (data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
        });
      });
      
      // Sort manually if orderBy failed
      ticketsList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      console.log('Loaded tickets:', ticketsList.length);
      setTickets(ticketsList);
    } catch (error) {
      console.error('Error loading tickets:', error);
      // Don't show error for empty collection, just set empty array
      if (error.code === 'permission-denied') {
        showNotification('Permission denied. Please check your authentication.', 'error');
      } else if (error.code === 'unavailable') {
        showNotification('Service temporarily unavailable. Please try again later.', 'error');
      } else {
        console.warn('Tickets collection might not exist yet, starting with empty list');
    setTickets([]);
      }
    } finally {
      setLoadingTickets(false);
    }
  };

  const generateTicketId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '#';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !description.trim()) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    if (!currentUser) {
      showNotification('You must be logged in to create a support ticket.', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const ticketId = generateTicketId();
      const ticketData = {
        ticketId: ticketId,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email.split('@')[0],
        subject: subject.trim(),
        category: category,
        priority: priority,
        description: description.trim(),
      status: 'open',
        createdAt: new Date(),
        updatedAt: new Date(),
      messages: [
        {
            id: Date.now().toString(),
            text: description.trim(),
          sender: 'client',
            senderName: currentUser.displayName || currentUser.email.split('@')[0],
            senderEmail: currentUser.email,
            timestamp: new Date(),
          isInitial: true
        }
      ]
    };

      const docRef = await addDoc(collection(db, 'tickets'), ticketData);
      
      // Create notification object for local state
      const newTicket = {
        id: docRef.id,
        ...ticketData,
        createdDate: new Date().toISOString().split('T')[0]
    };

    setTickets([newTicket, ...tickets]);
      
      // Notify admins about new ticket
      try {
        const adminUserIds = await getAdminUserIds();
        for (const adminId of adminUserIds) {
          await createNotification({
            userId: adminId,
            title: 'New Support Ticket',
            message: `${ticketData.userName} created a new ${priority} priority ticket: ${subject}`,
            icon: '◉',
            category: 'support',
            priority: priority,
            relatedId: docRef.id,
            relatedType: 'ticket',
            actionUrl: '/admin/support'
          });
        }
      } catch (notificationError) {
        console.error('Error sending notifications to admins:', notificationError);
      }
    
    // Reset form
    setSubject('');
      setCategory('general');
    setPriority('medium');
    setDescription('');
    setShowNewTicket(false);
      
      showNotification(`Support ticket ${ticketId} created successfully!`, 'success');
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      showNotification('Failed to create support ticket. Please try again.', 'error');
    } finally {
    setLoading(false);
    }
  };

  const setupMessagesListener = (ticketId) => {
    if (messagesListener) {
      messagesListener();
    }
    
    const ticketRef = doc(db, 'tickets', ticketId);
    const unsubscribe = onSnapshot(ticketRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setSelectedTicket(prev => ({
          ...prev,
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
        }));
        
        // Update ticket in main list
        setTickets(prev => prev.map(ticket => 
          ticket.id === ticketId 
            ? { 
                ...ticket, 
                ...data,
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
              }
            : ticket
        ));
      }
    });
    
    setMessagesListener(() => unsubscribe);
  };

  const handleViewThread = (ticket) => {
    setSelectedTicket(ticket);
    setShowChatModal(true);
    setupMessagesListener(ticket.id);
  };

  const closeChatModal = () => {
    setShowChatModal(false);
    setSelectedTicket(null);
    setNewMessage('');
    
    if (messagesListener) {
      messagesListener();
      setMessagesListener(null);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const message = {
        id: Date.now().toString(),
        text: newMessage.trim(),
        sender: 'client',
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: false
      };
      
      const currentMessages = selectedTicket.messages || [];
      const updatedMessages = [...currentMessages, message];
      
      await updateDoc(doc(db, 'tickets', selectedTicket.id), {
        messages: updatedMessages,
        updatedAt: new Date(),
        hasUnreadMessages: true
      });
      
      // Notify admins about new message
      try {
        const adminUserIds = await getAdminUserIds();
        for (const adminId of adminUserIds) {
          await createNotification({
            userId: adminId,
            title: 'New Support Message',
            message: `${currentUser.displayName || 'Client'} replied to ticket ${selectedTicket.ticketId}: ${selectedTicket.subject}`,
            icon: '💬',
            category: 'support',
            priority: 'medium',
            relatedId: selectedTicket.id,
            relatedType: 'ticket',
            actionUrl: '/admin/support'
          });
        }
      } catch (notificationError) {
        console.error('Error sending message notifications to admins:', notificationError);
      }
      
      setNewMessage('');
      showNotification('Message sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message. Please try again.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteTicket = (ticket) => {
    setTicketToDelete(ticket);
    setShowDeleteModal(true);
  };

  const confirmDeleteTicket = async () => {
    if (!ticketToDelete) return;
    
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'tickets', ticketToDelete.id));
      setTickets(tickets.filter(ticket => ticket.id !== ticketToDelete.id));
      showNotification('Support ticket deleted successfully.', 'success');
      setShowDeleteModal(false);
      setTicketToDelete(null);
    } catch (error) {
      console.error('Error deleting ticket:', error);
      showNotification('Failed to delete ticket. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteTicket = () => {
    setShowDeleteModal(false);
    setTicketToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#ff9800';
      case 'in-progress': return '#2196f3';
      case 'resolved': return '#4caf50';
      case 'closed': return '#666';
      default: return '#666';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#666';
    }
  };

  const canEditOrDelete = (ticket) => {
    return ticket.status === 'open' && (!ticket.messages || ticket.messages.length <= 1);
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  // Estado para monitorear el ancho de la ventana
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Manejar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determinar si estamos en un móvil
  const isMobile = windowWidth <= 640;
  const isTablet = windowWidth > 640 && windowWidth <= 768;
  
  return (
    <Layout>
      <div className="support-page" style={{
        padding: isMobile ? '0.75rem' : isTablet ? '1rem' : '1.5rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            maxWidth: '400px',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {notification.message}
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          gap: isMobile ? '1rem' : '0',
          marginBottom: '2rem' 
        }}>
          <h1 style={{ fontSize: isMobile ? '1.25rem' : isTablet ? '1.5rem' : '2rem', fontWeight: 'bold', color: '#2e7d32', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img 
              src="/supporticket.png" 
              alt="Support Tickets" 
              style={{ 
                width: '48px', 
                height: '48px', 
                objectFit: 'contain'
              }} 
            />
            Support Tickets
          </h1>
          <button 
            className="btn btn-primary"
            onClick={() => setShowNewTicket(!showNewTicket)}
            style={{ 
              fontSize: isMobile ? '0.8rem' : 'inherit',
              padding: isMobile ? '0.5rem 0.75rem' : 'inherit'
            }}
          >
            {showNewTicket ? 'Cancel' : '+ New Ticket'}
          </button>
        </div>

        {/* New Ticket Form */}
        {showNewTicket && (
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: window.innerWidth <= 640 ? '1rem' : '1.25rem' }}>Create Support Ticket</h3>
            
            <form onSubmit={handleSubmitTicket}>
              <div className="modern-form-group">
                <label className="modern-form-label">Subject *</label>
                <input
                  type="text"
                  className="modern-form-input"
                  placeholder="Brief description of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div className="modern-form-group">
                  <label className="modern-form-label">Category</label>
                  <select
                    className="modern-form-input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="general">General Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="billing">Billing & Payment</option>
                    <option value="service">Service Request</option>
                    <option value="complaint">Complaint</option>
                    <option value="other">Other</option>
                  </select>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">Priority</label>
                <select
                  className="modern-form-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="low">Low - General question</option>
                  <option value="medium">Medium - Need assistance</option>
                  <option value="high">High - Urgent issue</option>
                </select>
                </div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">Description *</label>
                <textarea
                  className="modern-form-input"
                  rows="5"
                  placeholder="Please describe your issue or question in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowNewTicket(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Existing Tickets */}
        <div className="card" style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', border: '1px solid #e5e7eb' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: 0, fontSize: window.innerWidth <= 640 ? '1rem' : '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              Your Support Tickets
            </h3>
            {tickets.length > 0 && (
              <span style={{ 
                fontSize: '0.875rem', 
                color: '#6b7280',
                background: '#f3f4f6',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px'
              }}>
                {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loadingTickets ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <div style={{ fontSize: window.innerWidth <= 640 ? '1.5rem' : '2rem', marginBottom: '1rem' }}>⏳</div>
              <p>Loading support tickets...</p>
            </div>
          ) : tickets.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {tickets.map((ticket) => (
                <div 
                  key={ticket.id} 
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Priority Indicator */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: getPriorityColor(ticket.priority),
                    borderRadius: '12px 0 0 12px'
                  }} />

                  <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'stretch' : 'flex-start', 
                    gap: '1rem' 
                  }}>
                    <div style={{ flex: 1 }}>
                      {/* Header */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: isMobile ? 'flex-start' : 'center', 
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: '0.75rem', 
                        marginBottom: '0.75rem' 
                      }}>
                        <div style={{ 
                          background: getStatusColor(ticket.status),
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem'
                        }}>
                    ◉
                  </div>
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '1.125rem', 
                            fontWeight: '600', 
                            color: '#1f2937',
                            marginBottom: '0.25rem'
                          }}>
                            {ticket.ticketId} - {ticket.subject}
                    </div>
                          
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem', 
                            flexWrap: 'wrap',
                            marginTop: isMobile ? '0.5rem' : 0
                          }}>
                            <span style={{ 
                              fontSize: '0.875rem', 
                              color: '#6b7280'
                            }}>
                              Created {ticket.createdDate}
                            </span>
                            
                      <span style={{ 
                        color: getPriorityColor(ticket.priority),
                        fontWeight: '600',
                        textTransform: 'capitalize',
                              fontSize: '0.875rem',
                              background: `${getPriorityColor(ticket.priority)}20`,
                              padding: '0.25rem 0.5rem',
                              borderRadius: '6px'
                      }}>
                        {ticket.priority} Priority
                      </span>

                      {ticket.messages && ticket.messages.length > 1 && (
                              <span style={{ 
                                fontSize: '0.875rem', 
                                color: '#6b7280',
                                background: '#f3f4f6',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px'
                              }}>
                                💬 {ticket.messages.length} messages
                              </span>
                            )}

                            {ticket.hasUnreadMessages && (
                              <span style={{ 
                                color: '#f44336', 
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                background: '#fee2e2',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px'
                              }}>
                                🔴 New Reply
                              </span>
                      )}
                    </div>
                  </div>
                      </div>

                      {/* Description */}
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: '#4b5563', 
                        lineHeight: '1.5',
                        marginBottom: '1rem',
                        background: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #f3f4f6'
                      }}>
                        {ticket.description.substring(0, 150)}
                        {ticket.description.length > 150 ? '...' : ''}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: isMobile ? 'row' : 'column', 
                      gap: '0.75rem', 
                      alignItems: isMobile ? 'center' : 'flex-end',
                      justifyContent: isMobile ? 'space-between' : 'flex-start',
                      width: isMobile ? '100%' : 'auto',
                      marginTop: isMobile ? '0.5rem' : 0
                    }}>
                      <span className={`status-badge-modern ${
                        ticket.status === 'open' ? 'status-warning-modern' :
                        ticket.status === 'in-progress' ? 'status-info-modern' :
                        ticket.status === 'resolved' ? 'status-success-modern' :
                        ticket.status === 'closed' ? 'status-pending-modern' :
                        'status-pending-modern'
                      }`}>
                        {ticket.status === 'in-progress' ? 'In Progress' : ticket.status}
                    </span>
                    
                      <div style={{ 
                        display: 'flex', 
                        gap: '0.5rem',
                        flexDirection: isMobile ? 'column' : 'row',
                        width: isMobile ? '100%' : 'auto'
                      }}>
                        {canEditOrDelete(ticket) && (
                        <button 
                            onClick={() => handleDeleteTicket(ticket)}
                            style={{ 
                              fontSize: '0.875rem', 
                              padding: '0.5rem 1rem',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#fecaca';
                              e.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#fee2e2';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            🗑️ Delete
                        </button>
                        )}
                        
                        <button 
                          onClick={() => handleViewThread(ticket)}
                          style={{ 
                            fontSize: isMobile ? '0.8rem' : '0.875rem', 
                            padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1.25rem',
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            width: isMobile ? '100%' : 'auto',
                            textAlign: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#1d4ed8';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#2563eb';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          💬 View Thread
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '3rem' }}>
              <div style={{ fontSize: window.innerWidth <= 640 ? '3rem' : '4rem', marginBottom: '1rem', opacity: 0.5 }}>◉</div>
              <h4 style={{ marginBottom: '0.5rem', color: '#374151', fontSize: window.innerWidth <= 640 ? '1rem' : '1.25rem', fontWeight: '600' }}>
                No support tickets yet
              </h4>
              <p style={{ color: '#6b7280', fontSize: window.innerWidth <= 640 ? '0.875rem' : '1rem', marginBottom: '1.5rem' }}>
                Create a support ticket if you need help or have questions.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowNewTicket(true)}
                style={{ 
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                + Create Your First Ticket
              </button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && ticketToDelete && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '8px' : '12px',
              padding: isMobile ? '1.5rem' : '2rem',
              maxWidth: '500px',
              width: isMobile ? '95%' : '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: isMobile ? '2.5rem' : '3rem', 
                marginBottom: isMobile ? '0.75rem' : '1rem',
                lineHeight: 1
              }}>⚠️</div>
              
              <h2 style={{ 
                margin: '0 0 1rem 0', 
                color: '#333', 
                fontSize: isMobile ? '1.25rem' : '1.5rem', 
                fontWeight: '600' 
              }}>
                Delete Support Ticket
              </h2>
              
              <p style={{ 
                margin: '0 0 1.5rem 0', 
                color: '#666', 
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete ticket <strong>{ticketToDelete.ticketId}</strong>?
                <br />
                <span style={{ fontSize: '0.875rem', color: '#999' }}>
                  "{ticketToDelete.subject}"
                </span>
                <br /><br />
                This action cannot be undone.
              </p>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column-reverse' : 'row',
                gap: '1rem', 
                justifyContent: 'center',
                marginTop: isMobile ? '1.5rem' : '2rem'
              }}>
                <button 
                  onClick={cancelDeleteTicket}
                  disabled={deleting}
                  style={{
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 2rem',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                    fontWeight: '600',
                    opacity: deleting ? 0.6 : 1,
                    width: isMobile ? '100%' : 'auto',
                    marginBottom: isMobile ? '0.5rem' : 0
                  }}
                >
                  Cancel
                </button>
                
                <button 
                  onClick={confirmDeleteTicket}
                  disabled={deleting}
                  style={{
                    background: deleting ? '#ccc' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 2rem',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                    fontWeight: '600',
                    width: isMobile ? '100%' : 'auto'
                  }}
                >
                  {deleting ? '🗑️ Deleting...' : '🗑️ Delete Ticket'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Modal */}
        {showChatModal && selectedTicket && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '0' : '12px',
              padding: '0',
              maxWidth: isMobile ? '100%' : '800px',
              width: isMobile ? '100%' : '90%',
              height: isMobile ? '100vh' : 'auto',
              maxHeight: isMobile ? '100vh' : '80vh',
              overflowY: 'auto',
              boxShadow: isMobile ? 'none' : '0 10px 25px rgba(0, 0, 0, 0.2)',
              position: isMobile ? 'fixed' : 'relative',
              top: isMobile ? '0' : 'auto',
              left: isMobile ? '0' : 'auto',
              right: isMobile ? '0' : 'auto',
              bottom: isMobile ? '0' : 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: isMobile ? '1rem' : '1.5rem 2rem',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa',
                position: isMobile ? 'sticky' : 'relative',
                top: 0,
                zIndex: 10
              }}>
                <div style={{ maxWidth: isMobile ? 'calc(100% - 40px)' : 'auto' }}>
                  <h2 style={{ 
                    margin: 0, 
                    color: '#2e7d32', 
                    fontSize: isMobile ? '1rem' : '1.25rem', 
                    fontWeight: '600',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}>
                    {isMobile ? selectedTicket.ticketId : `Ticket Details - ${selectedTicket.ticketId}`}
                  </h2>
                  <p style={{ 
                    margin: '0.25rem 0 0 0', 
                    color: '#666', 
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}>
                    {selectedTicket.subject}
                  </p>
                </div>
                <button 
                  onClick={closeChatModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ 
                padding: isMobile ? '1rem' : '2rem', 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column'
              }}>
                {/* Ticket Info */}
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: isMobile ? '0.75rem' : '1rem', 
                  borderRadius: '8px', 
                  marginBottom: '1rem',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: isMobile ? '0.5rem' : '1rem',
                    fontSize: isMobile ? '0.8rem' : 'inherit'
                  }}>
                    <div>
                      <strong>Status:</strong> 
                      <span style={{ 
                        color: getStatusColor(selectedTicket.status),
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        marginLeft: '0.5rem'
                      }}>
                        {selectedTicket.status}
                      </span>
                    </div>
                    <div>
                      <strong>Priority:</strong> 
                      <span style={{ 
                        color: getPriorityColor(selectedTicket.priority),
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        marginLeft: '0.5rem'
                      }}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <div>
                      <strong>Category:</strong> 
                      <span style={{ textTransform: 'capitalize', marginLeft: '0.5rem' }}>
                        {selectedTicket.category}
                      </span>
                    </div>
                    <div>
                      <strong>Created:</strong> 
                      <span style={{ marginLeft: '0.5rem' }}>
                        {selectedTicket.createdDate}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: isMobile ? '0.75rem' : '1rem',
                  marginBottom: isMobile ? '0.5rem' : '1rem',
                  border: '1px solid #e9ecef',
                  minHeight: isMobile ? '200px' : '300px',
                  height: isMobile ? 'calc(100vh - 300px)' : '400px',
                  maxHeight: isMobile ? 'calc(100vh - 300px)' : '400px',
                  overflowY: 'auto',
                  flex: 1
                }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem', fontWeight: '600' }}>
                    💬 Conversation Thread
                  </h4>
                  
                  {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {selectedTicket.messages.map((message, index) => (
                        <div
                          key={message.id || index}
                          style={{
                            display: 'flex',
                            justifyContent: message.sender === 'client' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div
                            style={{
                              maxWidth: isMobile ? '90%' : '80%',
                              padding: isMobile ? '0.6rem 0.8rem' : '0.75rem 1rem',
                              borderRadius: message.sender === 'client' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              backgroundColor: message.sender === 'client' ? '#2e7d32' : '#fff',
                              color: message.sender === 'client' ? 'white' : '#333',
                              border: message.sender === 'admin' ? '1px solid #ddd' : 'none',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              wordBreak: 'break-word'
                            }}
                          >
                            <div style={{ fontSize: '0.875rem', lineHeight: '1.4', marginBottom: '0.25rem' }}>
                              {message.text}
                            </div>
                            <div style={{
                              fontSize: '0.7rem',
                              opacity: 0.8,
                              textAlign: message.sender === 'client' ? 'right' : 'left'
                            }}>
                              {message.sender === 'client' ? 'You' : (message.senderName || 'Support Team')}
                              <br />
                              {message.timestamp?.toDate ? 
                                message.timestamp.toDate().toLocaleString() :
                                new Date(message.timestamp).toLocaleString()
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '0.875rem',
                      padding: '2rem'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                      <p>No messages yet.</p>
                    </div>
                  )}
                </div>

                {/* Message Input */}
                {selectedTicket.status !== 'closed' && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.75rem', 
                    alignItems: 'flex-end',
                    backgroundColor: '#fff',
                    padding: isMobile ? '0.75rem' : '1rem',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    position: isMobile ? 'sticky' : 'relative',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    marginTop: 'auto'
                  }}>
                    <textarea
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? '0.5rem' : '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: isMobile ? '0.85rem' : '0.875rem',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: isMobile ? '60px' : '80px',
                        maxHeight: isMobile ? '100px' : '150px',
                        fontFamily: 'inherit'
                      }}
                      disabled={sendingMessage}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      style={{
                        background: sendingMessage || !newMessage.trim() ? '#ccc' : '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem',
                        cursor: sendingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        fontWeight: '600',
                        minWidth: isMobile ? '60px' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {sendingMessage ? '📤 Sending...' : '📤 Send'}
                    </button>
                  </div>
                )}

                {selectedTicket.status === 'closed' && (
                  <div style={{
                    backgroundColor: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '0.875rem'
                  }}>
                    🔒 This ticket has been closed. No new messages can be sent.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SupportTickets;